import React from 'react';
import { Filter, SortAsc, Calendar } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { FeedbackFilters as Filters } from '../../types/feedback';

interface FeedbackFiltersProps {
  filters: Filters;
  onFilterChange: (filters: Filters) => void;
  products: string[];
  categories: string[];
}

export default function FeedbackFilters({
  filters,
  onFilterChange,
  products,
  categories
}: FeedbackFiltersProps) {
  const { theme } = useTheme();

  return (
    <div className={`
      p-4 rounded-lg mb-6 overflow-hidden
      ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}
    `}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-4">
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-blue-500" />
          <h3 className={`font-medium ${
            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Filters
          </h3>
        </div>
        <button
          onClick={() => onFilterChange({})}
          className={`text-sm ${
            theme === 'dark'
              ? 'text-gray-400 hover:text-gray-300'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Clear all
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <select
          value={filters.status || ''}
          onChange={(e) => onFilterChange({ ...filters, status: e.target.value as any })}
          className={`
            px-3 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
            ${theme === 'dark'
              ? 'bg-gray-700 border-gray-600 text-gray-100'
              : 'bg-white border-gray-300 text-gray-900'
            }
          `}
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="in-progress">In Progress</option>
          <option value="completed">Completed</option>
        </select>

        <select
          value={filters.product || ''}
          onChange={(e) => onFilterChange({ ...filters, product: e.target.value })}
          className={`
            px-3 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
            ${theme === 'dark'
              ? 'bg-gray-700 border-gray-600 text-gray-100'
              : 'bg-white border-gray-300 text-gray-900'
            }
          `}
        >
          <option value="">All Products</option>
          {products.map(product => (
            <option key={product} value={product}>{product}</option>
          ))}
        </select>

        <select
          value={filters.category || ''}
          onChange={(e) => onFilterChange({ ...filters, category: e.target.value })}
          className={`
            px-3 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
            ${theme === 'dark'
              ? 'bg-gray-700 border-gray-600 text-gray-100'
              : 'bg-white border-gray-300 text-gray-900'
            }
          `}
        >
          <option value="">All Categories</option>
          {categories.map(category => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>

        <select
          value={filters.sortBy || 'recent'}
          onChange={(e) => onFilterChange({ ...filters, sortBy: e.target.value as any })}
          className={`
            px-3 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
            ${theme === 'dark'
              ? 'bg-gray-700 border-gray-600 text-gray-100'
              : 'bg-white border-gray-300 text-gray-900'
            }
          `}
        >
          <option value="recent">Most Recent</option>
          <option value="popular">Most Popular</option>
          <option value="status">By Status</option>
        </select>
      </div>
    </div>
  );
}